import React from "react";
import { Skeleton, SkeletonText, SkeletonCard } from "../common";

interface DashboardSkeletonProps {
  userName?: string;
}

const DashboardSkeleton: React.FC<DashboardSkeletonProps> = ({ userName }) => {
  const getPersonalizedGreeting = () => {
    if (userName) {
      return `Welcome ${userName}!`;
    }
    return "Welcome!";
  };

  return (
    <div>
      {/* Personalized Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 text-primary">
          {getPersonalizedGreeting()}
        </h1>
        <p className="text-secondary mb-4">Loading your content library...</p>
      </div>

      {/* Status Banner Skeleton */}
      <div className="bg-accent/5 border border-accent/20 rounded-xl p-6 mb-8">
        <div className="flex items-start space-x-4">
          <Skeleton
            variant="circular"
            width={24}
            height={24}
            className="flex-shrink-0"
          />
          <div className="flex-1 space-y-3">
            <Skeleton width="40%" height={20} />
            <SkeletonText lines={2} />
          </div>
        </div>
      </div>

      {/* Content Library Skeleton */}
      <div className="bg-primary rounded-xl border border-secondary overflow-hidden">
        {/* Header */}
        <div className="px-8 py-6 border-b border-tertiary bg-secondary/50">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton width={200} height={24} />
              <Skeleton width={150} height={16} />
            </div>
            <div className="text-right space-y-2">
              <Skeleton width={80} height={14} />
              <Skeleton width={60} height={18} />
            </div>
          </div>
        </div>

        {/* Downloads Grid Skeleton */}
        <div className="p-8">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="border border-tertiary rounded-xl p-6 bg-secondary/20"
              >
                <div className="flex items-start space-x-6">
                  {/* Icon Skeleton */}
                  <Skeleton
                    width={64}
                    height={80}
                    rounded="lg"
                    className="flex-shrink-0"
                  />

                  {/* Content Skeleton */}
                  <div className="flex-1 min-w-0 space-y-4">
                    {/* Title and Description */}
                    <div className="space-y-3">
                      <Skeleton width="80%" height={20} />
                      <SkeletonText lines={2} />
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center gap-4">
                      <Skeleton width={80} height={24} rounded="full" />
                      <Skeleton width={40} height={16} />
                      <Skeleton width={50} height={16} />
                    </div>

                    {/* Changelog */}
                    <div className="p-3 bg-tertiary/10 rounded-lg space-y-2">
                      <Skeleton width={120} height={12} />
                      <Skeleton width="90%" height={14} />
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between">
                      <Skeleton width={100} height={12} />
                      <Skeleton width={100} height={36} rounded="lg" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSkeleton;
